"use client";

import React, { useState } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { ArrowUpRight, Zap, TrendingUp, Activity, Cpu, Database, Layers, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "@/components/ui/chart";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const efficiencyData = [{
  month: "Jan",
  manual: 100,
  ai: 100
}, {
  month: "Feb",
  manual: 102,
  ai: 130
}, {
  month: "Mar",
  manual: 105,
  ai: 170
}, {
  month: "Apr",
  manual: 108,
  ai: 240
}, {
  month: "May",
  manual: 110,
  ai: 320
}, {
  month: "Jun",
  manual: 112,
  ai: 450
}];
const taskDistributionData = [{
  category: "Writing",
  value: 45
}, {
  category: "Research",
  value: 30
}, {
  category: "Planning",
  value: 25
}];
const chartConfig = {
  manual: {
    label: "Manual Effort",
    color: "var(--chart-2)"
  },
  ai: {
    label: "AI Velocity",
    color: "var(--chart-1)"
  },
  value: {
    label: "Tasks",
    color: "var(--chart-3)"
  }
};
export default function Insights() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const getTiltStyle = (index: number) => {
    if (hoveredCard !== index) return {};
    return {
      transform: "perspective(1000px) rotateX(2deg) rotateY(-2deg) scale(1.02)",
      boxShadow: "0 20px 40px -10px var(--primary)",
      zIndex: 10
    };
  };
  return <section data-section-id="951" id="boundless-utility" ref={ref} className={`relative w-full py-24 overflow-hidden bg-background transition-opacity duration-1000 dark ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute bottom-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full bg-secondary/5 blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div className="mb-16 max-w-3xl">
          <Badge variant="outline" className="mb-4 font-mono text-primary border-primary/40 bg-primary/10 rounded-full tracking-wider">
            EVERYDAY USE CASES
          </Badge>
          <h2 className="text-4xl md:text-5xl font-default font-bold tracking-tight mb-6 text-foreground">
            Boundless <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--chart-1)] to-[var(--chart-3)]">Utility</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl font-default leading-relaxed">
            Real-time visualization of how OceanClaw transforms complex daily workflows into instant, automated results through your existing chat interface.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[minmax(180px,auto)]">
          
          {}
          <div className="md:col-span-8 row-span-2 transition-all duration-500 ease-out" onMouseEnter={() => setHoveredCard(1)} onMouseLeave={() => setHoveredCard(null)} style={getTiltStyle(1)}>
            <Card className="h-full bg-card/40 backdrop-blur-xl border-border/40 shadow-xl hover:border-primary/40 overflow-hidden flex flex-col rounded-3xl">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-2xl font-default font-bold flex items-center gap-2 text-foreground">
                      <Activity className="w-5 h-5 text-[var(--chart-1)]" />
                      Cognitive Velocity
                    </CardTitle>
                    <CardDescription className="font-mono text-xs mt-1 text-muted-foreground tracking-widest">
                      METRIC: AI_THROUGHPUT_EFFICIENCY
                    </CardDescription>
                  </div>
                  <Badge variant="secondary" className="bg-[var(--chart-1)]/20 text-[var(--chart-1)] hover:bg-[var(--chart-1)]/30 border-none rounded-full px-3">
                    +450% OUTPUT
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="flex-1 min-h-[300px]">
                <ChartContainer config={chartConfig} className="w-full h-full">
                  <AreaChart data={efficiencyData} margin={{
                  top: 10,
                  right: 10,
                  left: 0,
                  bottom: 0
                }}>
                    <defs>
                      <linearGradient id="fillAi" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--chart-1)" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="var(--chart-1)" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="fillManual" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--chart-2)" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="var(--chart-2)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" opacity={0.2} />
                    <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={value => value.slice(0, 3)} className="text-xs font-mono text-muted-foreground" />
                    <YAxis tickLine={false} axisLine={false} tickMargin={8} className="text-xs font-mono text-muted-foreground" />
                    <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
                    <Area dataKey="manual" type="monotone" fill="url(#fillManual)" fillOpacity={0.4} stroke="var(--chart-2)" strokeWidth={2} stackId="1" />
                    <Area dataKey="ai" type="monotone" fill="url(#fillAi)" fillOpacity={0.4} stroke="var(--chart-1)" strokeWidth={2} stackId="2" />
                    <ChartLegend content={<ChartLegendContent />} />
                  </AreaChart>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>

          {}
          <div className="md:col-span-4 row-span-1 transition-all duration-500 ease-out" onMouseEnter={() => setHoveredCard(2)} onMouseLeave={() => setHoveredCard(null)} style={getTiltStyle(2)}>
            <Card className="h-full bg-card/40 backdrop-blur-xl border-border/40 shadow-xl hover:border-primary/40 flex flex-col justify-center relative overflow-hidden group rounded-3xl">
              <div className="absolute inset-0 bg-gradient-to-br from-[var(--chart-1)]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <CardHeader className="pb-2">
                <CardDescription className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Personal Time Reclaimed</CardDescription>
                <CardTitle className="text-4xl font-default font-bold text-foreground flex items-baseline gap-2">
                  1,240 <span className="text-lg font-normal text-muted-foreground">hrs</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-sm text-[var(--chart-1)] font-default font-medium">
                  <Clock className="w-4 h-4" />
                  <span>Saved per user annually</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {}
          <div className="md:col-span-4 row-span-1 transition-all duration-500 ease-out" onMouseEnter={() => setHoveredCard(3)} onMouseLeave={() => setHoveredCard(null)} style={getTiltStyle(3)}>
            <Card className="h-full bg-card/40 backdrop-blur-xl border-border/40 shadow-xl hover:border-primary/40 flex flex-col justify-center relative overflow-hidden group rounded-3xl">
              <div className="absolute inset-0 bg-gradient-to-br from-[var(--chart-3)]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <CardHeader className="pb-2">
                <CardDescription className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Productivity ROI</CardDescription>
                <CardTitle className="text-4xl font-default font-bold text-foreground flex items-baseline gap-2">
                  4.8x <span className="text-lg font-normal text-muted-foreground">GAIN</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-sm text-[var(--chart-3)] font-default font-medium">
                  <TrendingUp className="w-4 h-4" />
                  <span>Workflow acceleration</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {}
          <div className="md:col-span-4 row-span-2 transition-all duration-500 ease-out" onMouseEnter={() => setHoveredCard(4)} onMouseLeave={() => setHoveredCard(null)} style={getTiltStyle(4)}>
            <Card className="h-full bg-primary/5 backdrop-blur-xl border-primary/20 shadow-xl hover:shadow-[0_0_40px_-10px_var(--primary)] hover:border-primary/40 flex flex-col relative overflow-hidden rounded-3xl">
              <div className="absolute -right-12 -top-12 w-40 h-40 bg-[var(--chart-1)]/20 rounded-full blur-3xl" />
              <CardHeader>
                <div className="w-10 h-10 rounded-xl bg-background flex items-center justify-center mb-4 border border-border/50 shadow-sm">
                  <Zap className="w-5 h-5 text-[var(--chart-1)]" />
                </div>
                <CardTitle className="text-xl font-default font-bold text-foreground">Consumer AI Mastery</CardTitle>
              </CardHeader>
              <CardContent className="flex-1">
                <p className="text-muted-foreground mb-6 leading-relaxed font-default">
                  Unlock the full potential of advanced LLMs through a simple chat interface. No prompts, no portals, just results.
                </p>
                <ul className="space-y-3">
                  {["Instant legal document review", "Automated life organization", "Smart research & shopping"].map((item, i) => <li key={i} className="flex items-center gap-3 text-sm font-default font-medium text-foreground" data-index={i}>
                      <div className="w-1.5 h-1.5 rounded-full bg-[var(--chart-1)]" />
                      {item}
                    </li>)}
                </ul>
              </CardContent>
              <CardFooter>
                <Button variant="ghost" className="w-full justify-between group hover:bg-background/40 rounded-xl text-primary font-default" asChild>
                  <Link to="#">
                    Explore All Cases
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </div>

          {}
          <div className="md:col-span-5 row-span-2 transition-all duration-500 ease-out" onMouseEnter={() => setHoveredCard(5)} onMouseLeave={() => setHoveredCard(null)} style={getTiltStyle(5)}>
            <Card className="h-full bg-card/40 backdrop-blur-xl border-border/40 shadow-xl hover:border-primary/40 flex flex-col rounded-3xl">
              <CardHeader>
                <CardTitle className="text-lg font-default font-bold flex items-center gap-2 text-foreground">
                  <Layers className="w-4 h-4 text-[var(--chart-2)]" />
                  Utility Distribution
                </CardTitle>
                <CardDescription className="font-mono text-xs text-muted-foreground tracking-widest">
                  EVERYDAY_INTERACTION_MODELS
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1 min-h-[200px]">
                <ChartContainer config={chartConfig} className="w-full h-full">
                  <BarChart data={taskDistributionData} layout="vertical" margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 0
                }}>
                    <CartesianGrid horizontal={false} stroke="var(--border)" opacity={0.2} />
                    <XAxis type="number" hide />
                    <YAxis dataKey="category" type="category" tickLine={false} axisLine={false} width={80} className="text-xs font-mono font-medium text-muted-foreground" />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="value" fill="var(--chart-3)" radius={[0, 8, 8, 0]} barSize={32} />
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>

          {}
          <div className="md:col-span-3 row-span-2 transition-all duration-500 ease-out" onMouseEnter={() => setHoveredCard(6)} onMouseLeave={() => setHoveredCard(null)} style={getTiltStyle(6)}>
            <Card className="h-full bg-gradient-to-b from-primary/10 to-background border-primary/20 shadow-xl hover:shadow-[0_0_40px_-10px_var(--primary)] flex flex-col justify-between relative overflow-hidden rounded-3xl">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1639322537228-f710d846310a?auto=format&fit=crop&q=80&w=1000')] bg-cover bg-center opacity-5 mix-blend-overlay" />
              
              <CardHeader>
                <Badge className="w-fit bg-primary text-primary-foreground hover:bg-primary/90 mb-2 rounded-full font-mono text-[10px] tracking-widest px-3 border-none">
                  START NOW
                </Badge>
                <CardTitle className="text-2xl font-default font-bold leading-tight text-foreground">
                  Ready to dive in?
                </CardTitle>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-background/60 border border-border/40 backdrop-blur-md">
                      <Cpu className="w-5 h-5 text-primary" />
                    </div>
                    <div className="text-sm">
                      <p className="font-default font-bold text-foreground">Private Setup</p>
                      <p className="text-xs text-muted-foreground font-default">iMessage or Slack</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-background/60 border border-border/40 backdrop-blur-md">
                      <Database className="w-5 h-5 text-primary" />
                    </div>
                    <div className="text-sm">
                      <p className="font-default font-bold text-foreground">Zero Config</p>
                      <p className="text-xs text-muted-foreground font-default">No prompts required</p>
                    </div>
                  </div>
                </div>
              </CardContent>

              <CardFooter>
                <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 rounded-xl font-default font-bold py-6" asChild>
                  <Link to="#">
                    Get Instant Access
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}